import React, { useState } from 'react';
import { KeyRoundIcon, Loader2Icon, LockIcon, MailIcon, UserIcon } from 'lucide-react';
interface JoinScreenProps {
  busy: boolean;
  error: string | null;
  onClearError: () => void;
  onJoin: (code: string, name: string) => void;
  onHostLogin: (email: string, password: string) => void;
}
type Tab = 'join' | 'host';
const fieldClass = 'w-full rounded-lg border border-line bg-raised py-3 pl-10 pr-3 text-sm text-white placeholder:text-muted/70 focus:border-gold focus:outline-none disabled:opacity-50';
export function JoinScreen({
  busy,
  error,
  onClearError,
  onJoin,
  onHostLogin
}: JoinScreenProps) {
  const [tab, setTab] = useState<Tab>('join');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const switchTab = (next: Tab) => {
    setTab(next);
    onClearError();
  };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (tab === 'join') onJoin(code, name);else onHostLogin(email, password);
  };
  const disabled = busy || (tab === 'join' ? code.trim().length === 0 || name.trim().length === 0 : email.trim().length === 0 || password.length === 0);
  return <main className="flex min-h-full w-full items-center justify-center bg-ink px-5 py-12 font-sans" data-id="element-244" data-mp="2s3d9gt4x32ra">
      <div className="w-full max-w-md" data-id="element-245" data-mp="2depil060nh7o">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-gold" data-id="element-246" data-mp="2ddnv1w7td292">
          Live draw
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-white sm:text-5xl" data-id="element-247" data-mp="2i6h06l772clo">
          {tab === 'join' ? 'Join the event' : 'Administrator sign in'}
        </h1>
        <p className="mt-3 text-sm text-muted" data-id="element-248" data-mp="29dnhsewgax3l">
          {tab === 'join' ? 'Enter the invitation code from your host and the name you want on the wheel.' : 'Sign in to set up an event, run the draw, and moderate the chat.'}
        </p>

        <div className="mt-8 flex gap-6 border-b border-line" role="tablist" aria-label="How you are joining" data-id="element-249" data-mp="1g9t5mwxrrt64">
          {(['join', 'host'] as Tab[]).map(t => <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => switchTab(t)} className={`-mb-px border-b-2 pb-3 text-sm font-semibold transition-colors duration-150 ease-emphasis focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold ${tab === t ? 'border-gold text-white' : 'border-transparent text-muted hover:text-body'}`} data-id="element-250" data-mp="16jqdt02dwl9d">
              {t === 'join' ? 'Participant' : 'Administrator'}
            </button>)}
        </div>

        <form onSubmit={submit} className="mt-7 space-y-4" data-id="element-251" data-mp="dwvuw7l7tty0">
          {tab === 'join' ? <>
              <div className="relative" data-id="element-252" data-mp="8ylzbvvkwrbv">
                <label className="sr-only" htmlFor="code" data-id="element-253" data-mp="2kkcyr5i7b0ew">
                  Invitation code
                </label>
                <KeyRoundIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-254" data-mp="2pjzcrh5uskor" />
                <input id="code" value={code} onChange={e => setCode(e.target.value.toUpperCase())} disabled={busy} autoComplete="off" placeholder="Invitation code" className={`${fieldClass} tracking-[0.18em]`} data-id="element-255" data-mp="2z9spilhqf5ul" />
              </div>
              <div className="relative" data-id="element-256" data-mp="3i7d2ovb4wfzd">
                <label className="sr-only" htmlFor="participant-name" data-id="element-257" data-mp="3i73bj4ecs2s3">
                  Participant name
                </label>
                <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-258" data-mp="1iwu3rnv78ita" />
                <input id="participant-name" value={name} onChange={e => setName(e.target.value)} disabled={busy} maxLength={40} placeholder="Participant name" className={fieldClass} data-id="element-259" data-mp="3ecsifhv4588a" />
              </div>
            </> : <>
              <div className="relative" data-id="element-260" data-mp="2vkg92b61l6on">
                <label className="sr-only" htmlFor="email" data-id="element-261" data-mp="2qln271l9gdxg">
                  Administrator email
                </label>
                <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-262" data-mp="34djpmsbzhzni" />
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={busy} autoComplete="email" placeholder="Administrator email" className={fieldClass} data-id="element-263" data-mp="2bvfhef7s6itl" />
              </div>
              <div className="relative" data-id="element-264" data-mp="1nsrblxvon0o4">
                <label className="sr-only" htmlFor="password" data-id="element-265" data-mp="1nu2elvhb04e6">
                  Password
                </label>
                <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-266" data-mp="1srvi2gr82kar" />
                <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} disabled={busy} autoComplete="current-password" placeholder="Password" className={fieldClass} data-id="element-267" data-mp="2lfrf5mkpee82" />
              </div>
            </>}

          {error && <p role="alert" className="rounded-lg border border-crimson/50 bg-crimson/10 px-4 py-3 text-sm text-[#F0A9A4]" data-id="element-268" data-mp="32plrfffx23lu">
              {error}
            </p>}

          <button type="submit" disabled={disabled} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-line disabled:text-muted disabled:active:scale-100" data-id="element-269" data-mp="2sz8yozxy744m">
            {busy && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-270" data-mp="2546bewesl8q9" />}
            {tab === 'join' ? 'Enter the event' : 'Sign in'}
          </button>
        </form>

        <p className="mt-6 text-xs leading-relaxed text-muted" data-id="element-271" data-mp="3azyanmkc8k3">
          {tab === 'join' ? 'Participants watch the draw live and join the chat — only the administrator can spin.' : 'You will choose the event name and invitation code on the next step.'}
        </p>
      </div>
    </main>;
}